package world.phantasmal.psolib.fileFormats.quest

import world.phantasmal.psolib.Episode

enum class ObjectType(
    override val uniqueName: String,
    /**
     * The valid area IDs per episode in which this object can appear.
     */
    val areaIds: Map<Episode, List<Int>>,
    val typeId: Short?,
    /**
     * Default object-specific properties.
     */
    override val properties: List<EntityProp> = emptyList(),
    /**
     * For `TObjCity_Season_*` decoration objects, the lobby event during which the game shows
     * this object. `null` for ordinary objects (always shown) and for `WelcomeBoard`, whose
     * event is unknown so it is treated as always-visible.
     */
    val lobbyEvent: LobbyEvent? = null,
) : EntityType {
    Unknown(
        uniqueName = "Unknown",
        areaIds = mapOf(),
        typeId = null,
    ),

    PlayerSet(
        uniqueName = "Player Set",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 0),
        ),
        typeId = 0,
        properties = listOf(
            EntityProp(name = "Slot ID", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Return flag", offset = 52, type = EntityPropType.I32),
        ),
    ),
    Particle(
        uniqueName = "Particle",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 0),
        ),
        typeId = 1,
        // TObjParticle params per newserv Map.cc and the PSOBB client constructor.
        properties = listOf(
            EntityProp(name = "Particle type", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Param 2", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Param 3", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Long range (1=1500)", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Param 5", offset = 56, type = EntityPropType.I32),
            EntityProp(name = "Param 6", offset = 60, type = EntityPropType.I32),
        ),
    ),
    Teleporter(
        uniqueName = "Teleporter",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 11, 12, 13, 14),
            Episode.II to listOf(0, 1, 2, 3, 4, 12, 13, 14, 15),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 0),
        ),
        typeId = 2,
        properties = listOf(
            EntityProp(name = "Destination floor", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Color (0=blue, 1=red)", offset = 60, type = EntityPropType.I32),
        ),
    ),
    Warp(
        uniqueName = "Warp",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 11, 12, 13, 14, 16, 17),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 0),
        ),
        typeId = 3,
        properties = listOf(
            EntityProp(name = "Destination x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Destination y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Destination z", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Dst. rotation y", offset = 52, type = EntityPropType.Angle),
        ),
    ),
    LightCollision(
        uniqueName = "Light Collision",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 16, 17),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 0),
        ),
        typeId = 4,
    ),
    Item(
        uniqueName = "Item",
        areaIds = mapOf(),
        typeId = 5,
    ),
    EnvSound(
        uniqueName = "Env Sound",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 13),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 0),
        ),
        typeId = 6,
        properties = listOf(
            EntityProp(name = "Radius", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "SE", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Volume", offset = 56, type = EntityPropType.I32),
        ),
    ),
    FogCollision(
        uniqueName = "Fog Collision",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 16, 17),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 0),
        ),
        typeId = 7,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Fog index no.", offset = 52, type = EntityPropType.I32),
            EntityProp(
                name = "Transition type",
                offset = 56,
                type = EntityPropType.I32,
                description = "0 = fade in, 1 = switch instantly.",
            ),
        ),
    ),
    EventCollision(
        uniqueName = "Event Collision",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 16, 17),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 0),
        ),
        typeId = 8,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Event ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    CharaCollision(
        uniqueName = "Chara Collision",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 8, 9, 10),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 9,
    ),
    ElementalTrap(
        uniqueName = "Elemental Trap",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9),
        ),
        typeId = 10,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Trap link", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Damage", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Subtype", offset = 56, type = EntityPropType.I32),
            EntityProp(name = "Delay", offset = 60, type = EntityPropType.I32),
        ),
    ),
    StatusTrap(
        uniqueName = "Status Trap",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9),
        ),
        typeId = 11,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Trap link", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Subtype", offset = 56, type = EntityPropType.I32),
            EntityProp(name = "Delay", offset = 60, type = EntityPropType.I32),
        ),
    ),
    HealTrap(
        uniqueName = "Heal Trap",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9),
        ),
        typeId = 12,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Trap link", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "HP", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Subtype", offset = 56, type = EntityPropType.I32),
            EntityProp(name = "Delay", offset = 60, type = EntityPropType.I32),
        ),
    ),
    LargeElementalTrap(
        uniqueName = "Large Elemental Trap",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9),
        ),
        typeId = 13,
        properties = listOf(
            EntityProp(name = "Radus", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Trap link", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Damage", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Subtype", offset = 56, type = EntityPropType.I32),
            EntityProp(name = "Delay", offset = 60, type = EntityPropType.I32),
        ),
    ),
    ObjRoomID(
        uniqueName = "Obj Room ID",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9),
        ),
        typeId = 14,
        properties = listOf(
            EntityProp(
                name = "Radius / 10", offset = 40, type = EntityPropType.F32,
                description = "Formerly SCL_TAMA. Effective radius = value * 10. Updates the player's current room ID when their XZ distance from this object is strictly less than that radius.",
            ),
            EntityProp(
                name = "Positive-side room ID", offset = 44, type = EntityPropType.F32,
                description = "Player's current room ID when the XZ vector from this object to the player has a positive dot product with the direction vector. Does not mean the next room in a sequence.",
            ),
            EntityProp(
                name = "Nonpositive-side room ID", offset = 48, type = EntityPropType.F32,
                description = "Player's current room ID when the XZ vector from this object to the player has a zero or negative dot product with the direction vector. Includes the dividing line; does not record the previously visited room.",
            ),
            EntityProp(
                name = "Direction angle", offset = 56, type = EntityPropType.Angle,
                description = "Rotates (0, 0, -1) around Y to choose the room ID from the player's position, not facing direction. Independent of the object's Rotation Y.",
            ),
            EntityProp(
                name = "Room update mode", offset = 60, type = EntityPropType.I32,
                description = "Ignored in v2 and earlier. In v3 and later, 65536 (0x00010000) writes only the first player room field and sets flag 0x02000000. In BB, this skips one automatic section update, then clears. Other values use the normal setter, also updating the second room field on special maps such as Forest and the CCA family.",
            ),
        ),
    ),
    Sensor(
        uniqueName = "Sensor",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 4, 5, 6, 7),
        ),
        typeId = 15,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    UnknownItem16(
        uniqueName = "Unknown Item (16)",
        areaIds = mapOf(),
        typeId = 16,
    ),
    LensFlare(
        uniqueName = "Lens Flare",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 8, 14),
        ),
        typeId = 17,
        properties = listOf(
            EntityProp(name = "Visibility radius", offset = 40, type = EntityPropType.F32),
        ),
    ),
    ScriptCollision(
        uniqueName = "Script Collision",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 0),
        ),
        typeId = 18,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Script label", offset = 52, type = EntityPropType.I32),
        ),
    ),
    HealRing(
        uniqueName = "Heal Ring",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 19,
    ),
    MapCollision(
        uniqueName = "Map Collision",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 8, 9, 10, 16, 17),
            Episode.II to listOf(0, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(0),
        ),
        typeId = 20,
        properties = listOf(
            EntityProp(name = "Wall type", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Flags", offset = 56, type = EntityPropType.I32),
        ),
    ),
    ScriptCollisionA(
        uniqueName = "Script Collision A",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 0),
        ),
        typeId = 21,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Script label", offset = 52, type = EntityPropType.I32),
        ),
    ),
    ItemLight(
        uniqueName = "Item Light",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 0),
        ),
        typeId = 22,
        properties = listOf(
            EntityProp(name = "Subtype", offset = 40, type = EntityPropType.F32),
        ),
    ),
    RadarCollision(
        uniqueName = "Radar Collision",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 23,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
        ),
    ),
    FogCollisionSW(
        uniqueName = "Fog Collision SW",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 24,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(
                name = "Fog on with switch",
                offset = 48,
                type = EntityPropType.F32,
                description = "1 or more: the fog is on while the switch flag is on. Otherwise it's on while the flag is off.",
            ),
            EntityProp(name = "Fog index no.", offset = 52, type = EntityPropType.I32),
            EntityProp(
                name = "Transition type",
                offset = 56,
                type = EntityPropType.I32,
                description = "0 = fade in, 1 = switch instantly.",
            ),
            EntityProp(name = "Switch ID", offset = 60, type = EntityPropType.I32),
        ),
    ),
    BossTeleporter(
        uniqueName = "Boss Teleporter",
        areaIds = mapOf(
            Episode.I to listOf(0, 2, 5, 7, 10),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(5, 6, 7, 8, 0),
        ),
        typeId = 25,
        properties = listOf(
            EntityProp(name = "Unlock ID", offset = 56, type = EntityPropType.I32),
        ),
    ),
    ImageBoard(
        uniqueName = "Image Board",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 26,
        properties = listOf(
            EntityProp(name = "Scale x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Scale y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Scale z", offset = 48, type = EntityPropType.F32),
        ),
    ),
    QuestWarp(
        uniqueName = "Quest Warp",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 11, 12, 13, 14),
            Episode.IV to listOf(9),
        ),
        typeId = 27,
        properties = listOf(
            EntityProp(name = "Player set ID", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Destination floor", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Color (0=blue, 1=red)", offset = 60, type = EntityPropType.I32),
        ),
    ),
    Epilogue(
        uniqueName = "Epilogue",
        areaIds = mapOf(
            Episode.I to listOf(14),
            Episode.II to listOf(13),
            Episode.IV to listOf(9),
        ),
        typeId = 28,
        properties = listOf(
            EntityProp(name = "Color", offset = 60, type = EntityPropType.I32),
        ),
    ),
    StarLight2D(
        uniqueName = "Star Light 2D",
        areaIds = mapOf(
            Episode.I to listOf(1),
        ),
        typeId = 29,
    ),
    LensFlare2(
        uniqueName = "Lens Flare 2",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 17),
            Episode.II to listOf(1, 2, 14),
            Episode.IV to listOf(1, 2, 3, 4, 5),
        ),
        typeId = 30,
    ),
    RadarHideCollision(
        uniqueName = "Radar Hide Collision",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 31,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
        ),
    ),
    BoxDetectObject(
        uniqueName = "Box Detect Object",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 0),
        ),
        typeId = 32,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Plate ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Item Type", offset = 56, type = EntityPropType.I32),
            EntityProp(name = "Amount", offset = 60, type = EntityPropType.I32),
        ),
    ),
    SymbolChatObject(
        uniqueName = "Symbol Chat Object",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 0),
        ),
        typeId = 33,
        // 3 spec slots, each packing (sc_index : low u16, switch_flag : high u16).
        // The display order matches qedit (FEdit.pas:561) and the spec semantics
        // are documented in newserv Map.cc TOSymbolchatColli.
        properties = listOf(
            EntityProp(name = "Radius",        offset = 40, type = EntityPropType.F32),
            EntityProp(name = "SC ID 1",       offset = 52, type = EntityPropType.U16),
            EntityProp(name = "SC Flag 1", offset = 54, type = EntityPropType.U16),
            EntityProp(name = "SC ID 2",       offset = 56, type = EntityPropType.U16),
            EntityProp(name = "SC Flag 2", offset = 58, type = EntityPropType.U16),
            EntityProp(name = "SC ID 3",       offset = 60, type = EntityPropType.U16),
            EntityProp(name = "SC Flag 3", offset = 62, type = EntityPropType.U16),
        ),
    ),
    TouchPlateObject(
        uniqueName = "Touch plate Object",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 34,
        properties = listOf(
            EntityProp(
                name = "Radius",
                offset = 40,
                type = EntityPropType.F32,
                description = "Activation radius = value + 10.",
            ),
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(
                name = "Sticky",
                offset = 56,
                type = EntityPropType.I32,
                description = "Negative: the switch flag turns off when the player leaves. Zero or more: it stays on.",
            ),
        ),
    ),
    TargetableObject(
        uniqueName = "Targetable Object",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 35,
        properties = listOf(
            EntityProp(name = "Active ID", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Target Type", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Switch ID", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "HP", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Script label", offset = 60, type = EntityPropType.I32),
        ),
    ),
    EffectObject(
        uniqueName = "Effect object",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(0, 1, 2, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.IV to listOf(0),
        ),
        typeId = 36,
        properties = listOf(
            EntityProp(name = "Damage Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Damage Multiplier", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Scale", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Switch-Off ID", offset = 56, type = EntityPropType.I32),
            EntityProp(
                name = "Repeat damage",
                offset = 60,
                type = EntityPropType.I32,
                description = "Nonzero: the effect can cause damage again later.",
            ),
        ),
    ),
    CountDownObject(
        uniqueName = "Count Down Object",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 0),
        ),
        typeId = 37,
        properties = listOf(
            EntityProp(name = "Switch ID 1", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Activation Switch", offset = 56, type = EntityPropType.I32),
            EntityProp(name = "Switch ID 2", offset = 60, type = EntityPropType.I32),
        ),
    ),
    ChatSensor(
        uniqueName = "Chat Sensor",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 0),
        ),
        typeId = 38,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(
                name = "Label or switch flag",
                offset = 52,
                type = EntityPropType.I32,
                description = "The switch flag to set or the quest label to call; angle x selects which.",
            ),
            EntityProp(name = "Trigger string", offset = 56, type = EntityPropType.I32),
        ),
    ),
    RadarIcon(
        uniqueName = "Radar Icon",
        areaIds = mapOf(
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 39,
        properties = listOf(
            EntityProp(name = "Width Scale", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Depth Scale", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Invisible", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Num Locks", offset = 56, type = EntityPropType.I32),
            EntityProp(name = "First Lock ID", offset = 60, type = EntityPropType.I32),
        ),
    ),
    EnvSoundEx(
        uniqueName = "Env Sound Ex",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 4, 5, 6, 7, 8, 9, 10, 13, 16, 17),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 0),
        ),
        typeId = 40,
        properties = listOf(
            EntityProp(name = "Sound Effect", offset = 52, type = EntityPropType.I32),
        ),
    ),
    EnvSoundGlobal(
        uniqueName = "Env Sound Global",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 4, 5, 6, 7, 8, 9, 10, 13, 16, 17),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 0),
        ),
        typeId = 41,
        properties = listOf(
            EntityProp(name = "Sound", offset = 52, type = EntityPropType.I32),
        ),
    ),
    MenuActivation(
        uniqueName = "Menu activation",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 64,
        properties = listOf(
            EntityProp(name = "Menu ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    TelepipeLocation(
        uniqueName = "Telepipe Location",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 65,
        properties = listOf(
            EntityProp(name = "Slot ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    BGMCollision(
        uniqueName = "BGM Collision",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 66,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(
                name = "Music",
                offset = 52,
                type = EntityPropType.I32,
                description = "0 = shop, 1 = guild, 2 = medical center, 3 = government, 4 = city (or its holiday variant), 5 = lab. Other values wrap around modulo 6.",
            ),
        ),
    ),
    MainRagolTeleporter(
        uniqueName = "Main Ragol Teleporter",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 67,
        properties = listOf(
            EntityProp(name = "Main warp type", offset = 56, type = EntityPropType.I32),
        ),
    ),
    LobbyTeleporter(
        uniqueName = "Lobby Teleporter",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 68,
    ),
    PrincipalWarp(
        uniqueName = "Principal warp",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 69,
        properties = listOf(
            EntityProp(name = "Destination x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Destination y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Destination z", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Dst. rotation y", offset = 52, type = EntityPropType.Angle),
            EntityProp(name = "Model", offset = 60, type = EntityPropType.I32),
        ),
    ),
    ShopDoor(
        uniqueName = "Shop Door",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 70,
    ),
    HuntersGuildDoor(
        uniqueName = "Hunter's Guild Door",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 71,
    ),
    TeleporterDoor(
        uniqueName = "Teleporter Door",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 72,
    ),
    MedicalCenterDoor(
        uniqueName = "Medical Center Door",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 73,
    ),
    Elevator(
        uniqueName = "Elevator",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 74,
    ),
    EasterEgg(
        uniqueName = "Easter Egg",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 75,
        lobbyEvent = LobbyEvent.Easter,
    ),
    ValentinesHeart(
        uniqueName = "Valentines Heart",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 76,
        lobbyEvent = LobbyEvent.Valentine,
    ),
    ChristmasTree(
        uniqueName = "Christmas Tree",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 77,
        lobbyEvent = LobbyEvent.Christmas,
    ),
    ChristmasWreath(
        uniqueName = "Christmas Wreath",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 78,
        lobbyEvent = LobbyEvent.Christmas,
    ),
    HalloweenPumpkin(
        uniqueName = "Halloween Pumpkin",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 79,
        lobbyEvent = LobbyEvent.Halloween,
    ),
    TwentyFirstCentury(
        uniqueName = "21st Century",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 80,
        lobbyEvent = LobbyEvent.NewYear,
    ),
    Sonic(
        uniqueName = "Sonic",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 81,
        lobbyEvent = LobbyEvent.Sonic,
        properties = listOf(
            EntityProp(name = "Model", offset = 52, type = EntityPropType.I32),
        ),
    ),
    WelcomeBoard(
        uniqueName = "Welcome Board",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 82,
    ),
    Firework(
        uniqueName = "Firework",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0, 16),
            Episode.IV to listOf(0),
        ),
        typeId = 83,
        lobbyEvent = LobbyEvent.NewYear,
        properties = listOf(
            EntityProp(
                name = "Area half-width x",
                offset = 40,
                type = EntityPropType.F32,
                description = "Fireworks launch within this distance of the object along x.",
            ),
            EntityProp(
                name = "Rise height",
                offset = 44,
                type = EntityPropType.F32,
                description = "Each firework rises this high plus a random 0 to 50.",
            ),
            EntityProp(
                name = "Area half-width z",
                offset = 48,
                type = EntityPropType.F32,
                description = "Fireworks launch within this distance of the object along z.",
            ),
            EntityProp(
                name = "Launch interval",
                offset = 52,
                type = EntityPropType.I32,
                description = "Wait between launches in frames: (value + 60) * 3 * a random fraction.",
            ),
        ),
    ),
    LobbyScreenDoor(
        uniqueName = "Lobby Screen Door",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 84,
    ),
    MainRagolTeleporterBattleInNextArea(
        uniqueName = "Main Ragol Teleporter (Challenge)",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 85,
        properties = listOf(
            EntityProp(name = "Destination floor", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Switch flag number", offset = 56, type = EntityPropType.I32),
        ),
    ),
    LabTeleporterDoor(
        uniqueName = "Lab Teleporter Door",
        areaIds = mapOf(
            Episode.II to listOf(0),
        ),
        typeId = 86,
        properties = listOf(
            EntityProp(
                name = "Switch flag",
                offset = 52,
                type = EntityPropType.I32,
                description = "Negative: always unlocked. 0 to 0x100: unlocked by this switch flag. Above 0x100: always locked.",
            ),
            EntityProp(name = "Model", offset = 56, type = EntityPropType.I32),
            EntityProp(
                name = "Sync mode",
                offset = 60,
                type = EntityPropType.I32,
                description = "Negative: once all switch flags are on, the door stays unlocked for good. Zero or more: it locks again when any switch flag turns off.",
            ),
        ),
    ),
    Pioneer2InvisibleTouchplate(
        uniqueName = "Pioneer 2 Invisible Touchplate",
        areaIds = mapOf(
            Episode.I to listOf(0),
            Episode.II to listOf(0),
            Episode.IV to listOf(0),
        ),
        typeId = 87,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
        ),
    ),
    ForestDoor(
        uniqueName = "Forest Door",
        areaIds = mapOf(
            Episode.I to listOf(1, 2),
        ),
        typeId = 128,
        properties = listOf(
            // param4 packs the switch flag in the low byte and the raw door display number in the
            // second-lowest byte. The renderer displays the latter modulo 10.
            EntityProp(name = "Door ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Door Display Number", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Unknown (param5)", offset = 56, type = EntityPropType.I32),
        ),
    ),
    ForestSwitch(
        uniqueName = "Forest Switch",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5),
            Episode.II to listOf(1, 2, 3, 4),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 129,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Color", offset = 60, type = EntityPropType.I32),
        ),
    ),
    LaserFence(
        uniqueName = "Laser Fence",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 130,
        properties = listOf(
            EntityProp(name = "Color", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Model", offset = 60, type = EntityPropType.I32),
        ),
    ),
    LaserSquareFence(
        uniqueName = "Laser Square Fence",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 131,
        properties = listOf(
            EntityProp(name = "Color", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Model", offset = 60, type = EntityPropType.I32),
        ),
    ),
    ForestLaserFenceSwitch(
        uniqueName = "Forest Laser Fence Switch",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 132,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Color", offset = 60, type = EntityPropType.I32),
        ),
    ),
    LightRays(
        uniqueName = "Light rays",
        areaIds = mapOf(
            Episode.I to listOf(1, 2),
            Episode.II to listOf(5, 6, 7, 8, 9),
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 133,
        properties = listOf(
            EntityProp(name = "Scale x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Scale y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Scale z", offset = 48, type = EntityPropType.F32),
        ),
    ),
    BlueButterfly(
        uniqueName = "Blue Butterfly",
        areaIds = mapOf(
            Episode.I to listOf(1, 2),
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 134,
    ),
    Probe(
        uniqueName = "Probe",
        areaIds = mapOf(
            Episode.I to listOf(1, 2),
        ),
        typeId = 135,
        properties = listOf(
            EntityProp(name = "Model", offset = 40, type = EntityPropType.F32),
        ),
    ),
    RandomTypeBox1(
        uniqueName = "Random Type Box 1",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7),
            Episode.II to listOf(10, 11, 13),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 136,
    ),
    ForestWeatherStation(
        uniqueName = "Forest Weather Station",
        areaIds = mapOf(
            Episode.I to listOf(1, 2),
        ),
        typeId = 137,
    ),
    Battery(
        uniqueName = "Battery",
        areaIds = mapOf(),
        typeId = 138,
    ),
    ForestConsole(
        uniqueName = "Forest Console",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 139,
        properties = listOf(
            EntityProp(name = "Script label", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Model", offset = 56, type = EntityPropType.I32),
        ),
    ),
    BlackSlidingDoor(
        uniqueName = "Black Sliding Door",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3),
        ),
        typeId = 140,
        properties = listOf(
            EntityProp(
                name = "Distance",
                offset = 40,
                type = EntityPropType.F32,
                description = "Total distance, divided evenly among the switch flags.",
            ),
            EntityProp(name = "Speed", offset = 44, type = EntityPropType.F32),
            EntityProp(
                name = "Switch flag",
                offset = 52,
                type = EntityPropType.I32,
                description = "Base switch flag: the door uses this flag and the ones after it.",
            ),
            EntityProp(
                name = "Switch count",
                offset = 56,
                type = EntityPropType.I32,
                description = "Number of switch flags (1 to 4).",
            ),
            EntityProp(
                name = "Movement effect",
                offset = 60,
                type = EntityPropType.I32,
                description = "0 = sparks and thud, 1 = grinding sound.",
            ),
        ),
    ),
    RicoMessagePod(
        uniqueName = "Rico Message Pod",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 13),
        ),
        typeId = 141,
        properties = listOf(
            EntityProp(name = "Active", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Message ID", offset = 56, type = EntityPropType.I32),
            EntityProp(name = "Script label", offset = 60, type = EntityPropType.I32),
        ),
    ),
    EnergyBarrier(
        uniqueName = "Energy Barrier",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 4, 5, 6, 7),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 142,
        properties = listOf(
            EntityProp(name = "Door ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    ForestRisingBridge(
        uniqueName = "Forest Rising Bridge",
        areaIds = mapOf(
            Episode.I to listOf(1, 2),
        ),
        typeId = 143,
        properties = listOf(
            EntityProp(name = "Door ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    SwitchNoneDoor(
        uniqueName = "Switch (none door)",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 6, 7, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 144,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    EnemyBoxGrey(
        uniqueName = "Enemy Box (Grey)",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7),
            Episode.II to listOf(10, 11),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 145,
        properties = listOf(
            // The event is in param4, not param1. Across 305 instances of this box and
            // 0x0093 in the offline map data, param1 only ever holds 1.0f, 1.0000002f or
            // 31.000023f, while param4 holds the event IDs that the matching map_*.evt
            // event tables actually define.
            EntityProp(
                name = "Event ID",
                offset = 52,
                type = EntityPropType.I32,
                description = "The wave event triggered when the box is opened.",
            ),
        ),
    ),
    FixedTypeBox(
        uniqueName = "Fixed Type Box",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 11, 12, 13, 14),
            Episode.II to listOf(10, 11, 13),
            Episode.IV to listOf(1, 2, 3, 4, 6, 7, 8, 9),
        ),
        typeId = 146,
        properties = listOf(
            EntityProp(
                name = "Random drop",
                offset = 40,
                type = EntityPropType.F32,
                description = "Greater than 0: drops any item (including this floor's box rares) or nothing, and the item fields are ignored. 0 or less: drops the item the item fields define.",
            ),
            EntityProp(
                name = "Exact item",
                offset = 48,
                type = EntityPropType.F32,
                description = "0: only the item kind from item data 1 is used and bonuses, grinds etc. are randomized as usual. Otherwise: drops exactly the item defined.",
            ),
            EntityProp(
                name = "Item data 1",
                offset = 52,
                type = EntityPropType.I32,
                description = "Item definition, part 1: item class and kind, plus weapon flags (untekked, present) and special.",
            ),
            EntityProp(
                name = "Item data 2",
                offset = 56,
                type = EntityPropType.I32,
                description = "Item definition, part 2: weapon grind and first attribute, armor or unit flags, or meseta amount (in tens).",
            ),
            EntityProp(
                name = "Item data 3",
                offset = 60,
                type = EntityPropType.I32,
                description = "Item definition, part 3: weapon attributes.",
            ),
        ),
    ),
    EnemyBoxBrown(
        uniqueName = "Enemy Box (Brown)",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7),
            Episode.II to listOf(10, 11),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 147,
        properties = listOf(
            // Same parameters as 0x0091; see the note there.
            EntityProp(
                name = "Event ID",
                offset = 52,
                type = EntityPropType.I32,
                description = "The wave event triggered when the box is opened.",
            ),
        ),
    ),
    EmptyTypeBox(
        uniqueName = "Empty Type Box",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 149,
        // TObjContainerNoItem is the always-empty box and takes no parameters. The
        // "Event ID" this used to expose was the 0x0091 entry copied verbatim, pointing at
        // param1 -- a slot the surrounding box types keep a float in. This type does not
        // occur in any offline map data, so nothing here is confirmed against real objects;
        // exposing no property is the only option that does not assert an unverified offset.
    ),
    LaserFenceEx(
        uniqueName = "Laser Fence Ex",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 150,
        properties = listOf(
            EntityProp(name = "Color", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Collision depth", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Collision width", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Model", offset = 60, type = EntityPropType.I32),
        ),
    ),
    LaserSquareFenceEx(
        uniqueName = "Laser Square Fence Ex",
        areaIds = mapOf(),
        typeId = 151,
        properties = listOf(
            EntityProp(name = "Color", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Collision depth", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Collision width", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Model", offset = 60, type = EntityPropType.I32),
        ),
    ),
    FloorPanel1(
        uniqueName = "Floor Panel 1",
        areaIds = mapOf(
            Episode.I to listOf(3, 4, 5, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 192,
        properties = listOf(
            EntityProp(name = "Scale x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Scale y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Scale z", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(
                name = "Activation mode",
                offset = 56,
                type = EntityPropType.I32,
                description = "Negative: temporary, the switch flag turns off when the player steps off. Zero or more: permanent. Offline single player is always permanent.",
            ),
        ),
    ),
    Caves4ButtonDoor(
        uniqueName = "Caves 4 Button door",
        areaIds = mapOf(
            Episode.I to listOf(3, 4, 5),
        ),
        typeId = 193,
        properties = listOf(
            EntityProp(
                name = "Door ID",
                offset = 52,
                type = EntityPropType.I32,
                description = "Base switch flag: the door needs this flag and the ones after it. Negative: always unlocked.",
            ),
            EntityProp(
                name = "Unused switches",
                offset = 56,
                type = EntityPropType.I32,
                description = "4 minus the number of switch flags the door needs: 0 = 4 switches, 1 = 3 switches, and so on.",
            ),
            EntityProp(
                name = "Sync mode",
                offset = 60,
                type = EntityPropType.I32,
                description = "Negative: once all switch flags are on, the door stays unlocked for good. Zero or more: it locks again when any switch flag turns off (offline single player always behaves as negative).",
            ),
        ),
    ),
    CavesNormalDoor(
        uniqueName = "Caves Normal door",
        areaIds = mapOf(
            Episode.I to listOf(3, 4, 5),
        ),
        typeId = 194,
        properties = listOf(
            EntityProp(name = "Door ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    CavesSmashingPillar(
        uniqueName = "Caves Smashing Pillar",
        areaIds = mapOf(
            Episode.I to listOf(3, 4, 5),
            Episode.II to listOf(1, 2, 3, 4, 17),
        ),
        typeId = 195,
        properties = listOf(
            EntityProp(name = "Duration", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Damage", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Global Sync", offset = 56, type = EntityPropType.I32),
            EntityProp(name = "Behavior", offset = 60, type = EntityPropType.I32),
        ),
    ),
    CavesSign1(
        uniqueName = "Caves Sign 1",
        areaIds = mapOf(
            Episode.I to listOf(4, 5),
        ),
        typeId = 196,
    ),
    CavesSign2(
        uniqueName = "Caves Sign 2",
        areaIds = mapOf(
            Episode.I to listOf(4, 5),
        ),
        typeId = 197,
    ),
    CavesSign3(
        uniqueName = "Caves Sign 3",
        areaIds = mapOf(
            Episode.I to listOf(4, 5),
        ),
        typeId = 198,
    ),
    HexagonalTank(
        uniqueName = "Hexagonal Tank",
        areaIds = mapOf(
            Episode.I to listOf(4, 5),
        ),
        typeId = 199,
    ),
    BrownPlatform(
        uniqueName = "Brown Platform",
        areaIds = mapOf(
            Episode.I to listOf(4, 5),
        ),
        typeId = 200,
    ),
    WarningLightObject(
        uniqueName = "Warning Light Object",
        areaIds = mapOf(
            Episode.I to listOf(4, 5),
            Episode.IV to listOf(5),
        ),
        typeId = 201,
        properties = listOf(
            EntityProp(
                name = "Rotation speed",
                offset = 40,
                type = EntityPropType.F32,
                description = "Degrees per frame.",
            ),
        ),
    ),
    Rainbow(
        uniqueName = "Rainbow",
        areaIds = mapOf(
            Episode.I to listOf(4),
        ),
        typeId = 203,
        properties = listOf(
            EntityProp(name = "Scale x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Scale y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Scale z", offset = 48, type = EntityPropType.F32),
        ),
    ),
    FloatingJellyfish(
        uniqueName = "Floating Jellyfish",
        areaIds = mapOf(
            Episode.I to listOf(4),
            Episode.II to listOf(10, 11),
        ),
        typeId = 204,
        properties = listOf(
            EntityProp(
                name = "Retreat radius",
                offset = 40,
                type = EntityPropType.F32,
                description = "The jellyfish retreats when any player comes within this distance.",
            ),
            EntityProp(name = "Move radius", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Rebirth radius", offset = 48, type = EntityPropType.F32),
        ),
    ),
    FloatingDragonfly(
        uniqueName = "Floating Dragonfly",
        areaIds = mapOf(
            Episode.I to listOf(4, 16),
            Episode.II to listOf(3, 4),
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 205,
        properties = listOf(
            EntityProp(
                name = "Step size",
                offset = 48,
                type = EntityPropType.F32,
                description = "How far it moves per step, and how close it must get for a waypoint to count as reached.",
            ),
        ),
    ),
    CavesSwitchDoor(
        uniqueName = "Caves Switch Door",
        areaIds = mapOf(
            Episode.I to listOf(3, 4, 5),
        ),
        typeId = 206,
        properties = listOf(
            EntityProp(name = "Door ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    RobotRechargeStation(
        uniqueName = "Robot Recharge Station",
        areaIds = mapOf(
            Episode.I to listOf(3, 4, 5, 6, 7),
            Episode.II to listOf(17),
        ),
        typeId = 207,
        properties = listOf(
            EntityProp(name = "Quest register number", offset = 52, type = EntityPropType.I32),
        ),
    ),
    CavesCakeShop(
        uniqueName = "Caves Cake Shop",
        areaIds = mapOf(
            Episode.I to listOf(5),
        ),
        typeId = 208,
    ),
    Caves1SmallRedRock(
        uniqueName = "Caves 1 Small Red Rock",
        areaIds = mapOf(
            Episode.I to listOf(3),
        ),
        typeId = 209,
    ),
    Caves1MediumRedRock(
        uniqueName = "Caves 1 Medium Red Rock",
        areaIds = mapOf(
            Episode.I to listOf(3),
        ),
        typeId = 210,
    ),
    Caves1LargeRedRock(
        uniqueName = "Caves 1 Large Red Rock",
        areaIds = mapOf(
            Episode.I to listOf(3),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 211,
    ),
    Caves2SmallRock1(
        uniqueName = "Caves 2 Small Rock 1",
        areaIds = mapOf(
            Episode.I to listOf(4),
        ),
        typeId = 212,
    ),
    Caves2MediumRock1(
        uniqueName = "Caves 2 Medium Rock 1",
        areaIds = mapOf(
            Episode.I to listOf(4),
        ),
        typeId = 213,
    ),
    Caves2LargeRock1(
        uniqueName = "Caves 2 Large Rock 1",
        areaIds = mapOf(
            Episode.I to listOf(4),
        ),
        typeId = 214,
    ),
    Caves2SmallRock2(
        uniqueName = "Caves 2 Small Rock 2",
        areaIds = mapOf(
            Episode.I to listOf(4),
        ),
        typeId = 215,
    ),
    Caves2MediumRock2(
        uniqueName = "Caves 2 Medium Rock 2",
        areaIds = mapOf(
            Episode.I to listOf(4),
        ),
        typeId = 216,
    ),
    Caves2LargeRock2(
        uniqueName = "Caves 2 Large Rock 2",
        areaIds = mapOf(
            Episode.I to listOf(4),
        ),
        typeId = 217,
    ),
    Caves3SmallRock(
        uniqueName = "Caves 3 Small Rock",
        areaIds = mapOf(
            Episode.I to listOf(5),
        ),
        typeId = 218,
    ),
    Caves3MediumRock(
        uniqueName = "Caves 3 Medium Rock",
        areaIds = mapOf(
            Episode.I to listOf(5),
        ),
        typeId = 219,
    ),
    Caves3LargeRock(
        uniqueName = "Caves 3 Large Rock",
        areaIds = mapOf(
            Episode.I to listOf(5),
        ),
        typeId = 220,
    ),
    FloorPanel2(
        uniqueName = "Floor Panel 2",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 222,
        properties = listOf(
            EntityProp(name = "Scale x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Scale y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Scale z", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(
                name = "Model",
                offset = 56,
                type = EntityPropType.I32,
                description = "Low 16 bits, in VR Temple and VR Spaceship: 0 = Caves button, otherwise Mines button.",
            ),
            EntityProp(
                name = "Sound",
                offset = 60,
                type = EntityPropType.I32,
                description = "Low 16 bits: the sound to play (0 = none). High 16 bits: 1 = only when the switch turns on, otherwise when it turns on and off.",
            ),
        ),
    ),
    DestructableRockCaves1(
        uniqueName = "Destructable Rock (Caves 1)",
        areaIds = mapOf(
            Episode.I to listOf(3),
        ),
        typeId = 223,
        properties = listOf(
            EntityProp(name = "Switch flag number", offset = 52, type = EntityPropType.I32),
        ),
    ),
    DestructableRockCaves2(
        uniqueName = "Destructable Rock (Caves 2)",
        areaIds = mapOf(
            Episode.I to listOf(4),
        ),
        typeId = 224,
    ),
    DestructableRockCaves3(
        uniqueName = "Destructable Rock (Caves 3)",
        areaIds = mapOf(
            Episode.I to listOf(5),
        ),
        typeId = 225,
    ),
    MinesDoor(
        uniqueName = "Mines Door",
        areaIds = mapOf(
            Episode.I to listOf(6, 7),
        ),
        typeId = 256,
        properties = listOf(
            EntityProp(
                name = "Door ID",
                offset = 52,
                type = EntityPropType.I32,
                description = "Base switch flag: the door needs this flag and the ones after it. Negative: always unlocked.",
            ),
            EntityProp(
                name = "Unused switches",
                offset = 56,
                type = EntityPropType.I32,
                description = "4 minus the number of switch flags the door needs: 0 = 4 switches, 1 = 3 switches, and so on.",
            ),
        ),
    ),
    FloorPanel3(
        uniqueName = "Floor Panel 3",
        areaIds = mapOf(
            Episode.I to listOf(1, 2, 6, 7, 16, 17),
            Episode.II to listOf(1, 2, 3, 4),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 257,
        properties = listOf(
            EntityProp(name = "Scale x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Scale y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Scale z", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(
                name = "Activation mode",
                offset = 56,
                type = EntityPropType.I32,
                description = "Negative: temporary, the switch flag turns off when the player steps off. Zero or more: permanent. Offline single player is always permanent.",
            ),
        ),
    ),
    MinesSwitchDoor(
        uniqueName = "Mines Switch Door",
        areaIds = mapOf(
            Episode.I to listOf(6, 7),
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 258,
        properties = listOf(
            EntityProp(
                name = "Door ID",
                offset = 52,
                type = EntityPropType.I32,
                description = "Switch flag that unlocks the door. Negative: always unlocked. Above 0x100: always locked.",
            ),
        ),
    ),
    LargeCryoTube(
        uniqueName = "Large Cryo-Tube",
        areaIds = mapOf(
            Episode.I to listOf(6, 7),
            Episode.II to listOf(17),
        ),
        typeId = 259,
    ),
    ComputerLikeCalus(
        uniqueName = "Computer (like calus)",
        areaIds = mapOf(
            Episode.I to listOf(6, 7),
            Episode.II to listOf(17),
        ),
        typeId = 260,
        properties = listOf(
            EntityProp(name = "Script label", offset = 60, type = EntityPropType.I32),
        ),
    ),
    GreenScreenOpeningAndClosing(
        uniqueName = "Green Screen opening and closing",
        areaIds = mapOf(
            Episode.I to listOf(6, 7),
            Episode.II to listOf(17),
        ),
        typeId = 261,
        properties = listOf(
            EntityProp(
                name = "Initial state",
                offset = 52,
                type = EntityPropType.I32,
                description = "0 to 3.",
            ),
        ),
    ),
    FloatingRobot(
        uniqueName = "Floating Robot",
        areaIds = mapOf(
            Episode.I to listOf(6, 7),
        ),
        typeId = 262,
    ),
    FloatingBlueLight(
        uniqueName = "Floating Blue Light",
        areaIds = mapOf(
            Episode.I to listOf(6, 7),
        ),
        typeId = 263,
        properties = listOf(
            EntityProp(
                name = "Float frequency",
                offset = 52,
                type = EntityPropType.I32,
                description = "Cycles per second = value * 0.1 + 1.0.",
            ),
            EntityProp(
                name = "Float distance",
                offset = 56,
                type = EntityPropType.I32,
                description = "Maximum float distance = value * 0.1 + 0.5.",
            ),
            EntityProp(
                name = "Rotation speed",
                offset = 60,
                type = EntityPropType.I32,
                description = "Angle units per frame (0x10000 = one full turn).",
            ),
        ),
    ),
    SelfDestructingObject1(
        uniqueName = "Self Destructing Object 1",
        areaIds = mapOf(
            Episode.I to listOf(6, 7),
        ),
        typeId = 264,
        properties = listOf(
            EntityProp(name = "Radius delta", offset = 40, type = EntityPropType.F32),
        ),
    ),
    SelfDestructingObject2(
        uniqueName = "Self Destructing Object 2",
        areaIds = mapOf(
            Episode.I to listOf(6, 7),
        ),
        typeId = 265,
    ),
    SelfDestructingObject3(
        uniqueName = "Self Destructing Object 3",
        areaIds = mapOf(
            Episode.I to listOf(6, 7),
        ),
        typeId = 266,
    ),
    SparkMachine(
        uniqueName = "Spark Machine",
        areaIds = mapOf(
            Episode.I to listOf(6, 7),
        ),
        typeId = 267,
        properties = listOf(
            EntityProp(name = "Scale x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Scale y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Scale z", offset = 48, type = EntityPropType.F32),
        ),
    ),
    MinesLargeFlashingCrate(
        uniqueName = "Mines Large Flashing Crate",
        areaIds = mapOf(
            Episode.I to listOf(6, 7),
        ),
        typeId = 268,
        properties = listOf(
            EntityProp(
                name = "Gray box",
                offset = 44,
                type = EntityPropType.F32,
                description = "Greater than 0: a gray box sits in the left half of the stall.",
            ),
        ),
    ),
    RuinsSeal(
        uniqueName = "Ruins Seal",
        areaIds = mapOf(
            Episode.I to listOf(13),
        ),
        typeId = 304,
    ),
    RuinsTeleporter(
        uniqueName = "Ruins Teleporter",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 320,
        properties = listOf(
            EntityProp(name = "Destination floor", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Color (<0=red, >=0=blue)", offset = 60, type = EntityPropType.I32),
        ),
    ),
    RuinsWarpSiteToSite(
        uniqueName = "Ruins Warp (Site to Site)",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 321,
        properties = listOf(
            EntityProp(name = "Destination x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Destination y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Destination z", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Dst. rotation y", offset = 52, type = EntityPropType.Angle),
        ),
    ),
    RuinsSwitch(
        uniqueName = "Ruins Switch",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 322,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    FloorPanel4(
        uniqueName = "Floor Panel 4",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 323,
        properties = listOf(
            EntityProp(name = "Scale x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Scale y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Scale z", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Plate ID", offset = 52, type = EntityPropType.I32),
            EntityProp(
                name = "Activation mode",
                offset = 56,
                type = EntityPropType.I32,
                description = "Negative: temporary, the switch flag turns off when the player steps off. Zero or more: permanent. Offline single player is always permanent.",
            ),
        ),
    ),
    Ruins1Door(
        uniqueName = "Ruins 1 Door",
        areaIds = mapOf(
            Episode.I to listOf(8),
        ),
        typeId = 324,
        properties = listOf(
            EntityProp(name = "Door ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    Ruins3Door(
        uniqueName = "Ruins 3 Door",
        areaIds = mapOf(
            Episode.I to listOf(10),
        ),
        typeId = 325,
        properties = listOf(
            EntityProp(name = "Door ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    Ruins2Door(
        uniqueName = "Ruins 2 Door",
        areaIds = mapOf(
            Episode.I to listOf(9),
        ),
        typeId = 326,
        properties = listOf(
            EntityProp(name = "Door ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    Ruins11ButtonDoor(
        uniqueName = "Ruins 1-1 Button Door",
        areaIds = mapOf(
            Episode.I to listOf(8),
        ),
        typeId = 327,
        properties = listOf(
            EntityProp(name = "Door ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    Ruins21ButtonDoor(
        uniqueName = "Ruins 2-1 Button Door",
        areaIds = mapOf(
            Episode.I to listOf(9),
        ),
        typeId = 328,
        properties = listOf(
            EntityProp(name = "Door ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    Ruins31ButtonDoor(
        uniqueName = "Ruins 3-1 Button Door",
        areaIds = mapOf(
            Episode.I to listOf(10),
        ),
        typeId = 329,
        properties = listOf(
            EntityProp(name = "Door ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    Ruins4ButtonDoor(
        uniqueName = "Ruins 4-Button Door",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 330,
        properties = listOf(
            EntityProp(
                name = "Door ID",
                offset = 52,
                type = EntityPropType.I32,
                description = "Base switch flag: the door needs this flag and the 3 after it.",
            ),
            EntityProp(
                name = "Sync mode",
                offset = 60,
                type = EntityPropType.I32,
                description = "Negative: once all switch flags are on, the door stays unlocked for good. Zero or more: it locks again when any switch flag turns off (offline single player always behaves as negative).",
            ),
        ),
    ),
    Ruins2ButtonDoor(
        uniqueName = "Ruins 2-Button Door",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 331,
        properties = listOf(
            EntityProp(
                name = "Door ID",
                offset = 52,
                type = EntityPropType.I32,
                description = "Base switch flag: the door needs this flag and the one after it.",
            ),
            EntityProp(
                name = "Sync mode",
                offset = 60,
                type = EntityPropType.I32,
                description = "Negative: once all switch flags are on, the door stays unlocked for good. Zero or more: it locks again when any switch flag turns off (offline single player always behaves as negative).",
            ),
        ),
    ),
    RuinsSensor(
        uniqueName = "Ruins Sensor",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 332,
        properties = listOf(
            EntityProp(name = "Activation radius delta", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Switch flag number", offset = 52, type = EntityPropType.I32),
            EntityProp(
                name = "Activation condition",
                offset = 56,
                type = EntityPropType.I32,
                description = "Negative: needs a nearby player and a local client ID check. Zero or more: needs a nearby player and game flag 0x00400000.",
            ),
            EntityProp(name = "Texture index", offset = 60, type = EntityPropType.I32),
        ),
    ),
    RuinsFenceSwitch(
        uniqueName = "Ruins Fence Switch",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 333,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Color", offset = 56, type = EntityPropType.I32),
        ),
    ),
    RuinsLaserFence4x2(
        uniqueName = "Ruins Laser Fence 4x2",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 334,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Color", offset = 56, type = EntityPropType.I32),
        ),
    ),
    RuinsLaserFence6x2(
        uniqueName = "Ruins Laser Fence 6x2",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 335,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Color", offset = 56, type = EntityPropType.I32),
        ),
    ),
    RuinsLaserFence4x4(
        uniqueName = "Ruins Laser Fence 4x4",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 336,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Color", offset = 56, type = EntityPropType.I32),
        ),
    ),
    RuinsLaserFence6x4(
        uniqueName = "Ruins Laser Fence 6x4",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 337,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Color", offset = 56, type = EntityPropType.I32),
        ),
    ),
    RuinsPoisonBlob(
        uniqueName = "Ruins poison Blob",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
            Episode.II to listOf(5, 6, 7, 8, 9),
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 338,
        properties = listOf(
            EntityProp(
                name = "Idle duration",
                offset = 40,
                type = EntityPropType.F32,
                description = "Maximum idle time in frames = value + 299; ends early when a player comes within 80 units.",
            ),
            EntityProp(
                name = "Spew duration",
                offset = 44,
                type = EntityPropType.F32,
                description = "Poison-spewing time in frames = value + 209.",
            ),
            EntityProp(
                name = "Poison radius squared",
                offset = 48,
                type = EntityPropType.F32,
                description = "Squared poison radius = value + 399.",
            ),
            EntityProp(
                name = "Particle interval",
                offset = 60,
                type = EntityPropType.I32,
                description = "Frames between particle bursts while spewing = value + 4.",
            ),
        ),
    ),
    RuinsPillarTrap(
        uniqueName = "Ruins Pillar Trap",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
            Episode.II to listOf(1, 2, 3, 4),
        ),
        typeId = 339,
        properties = listOf(
            EntityProp(name = "Trigger radius delta", offset = 40, type = EntityPropType.F32),
        ),
    ),
    PopupTrapNoTech(
        uniqueName = "Popup Trap (No Tech)",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 340,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
        ),
    ),
    RuinsCrystal(
        uniqueName = "Ruins Crystal",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 341,
        properties = listOf(
            EntityProp(name = "Script label", offset = 60, type = EntityPropType.I32),
        ),
    ),
    Monument(
        uniqueName = "Monument",
        areaIds = mapOf(
            Episode.I to listOf(2, 4, 7),
        ),
        typeId = 342,
    ),
    RuinsRock1(
        uniqueName = "Ruins Rock 1",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 345,
    ),
    RuinsRock2(
        uniqueName = "Ruins Rock 2",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 346,
    ),
    RuinsRock3(
        uniqueName = "Ruins Rock 3",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 347,
    ),
    RuinsRock4(
        uniqueName = "Ruins Rock 4",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 348,
    ),
    RuinsRock5(
        uniqueName = "Ruins Rock 5",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 349,
    ),
    RuinsRock6(
        uniqueName = "Ruins Rock 6",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 350,
    ),
    RuinsRock7(
        uniqueName = "Ruins Rock 7",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 351,
    ),
    Poison(
        uniqueName = "Poison",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10, 13),
            Episode.II to listOf(3, 4, 10, 11),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 352,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(
                name = "Poison power",
                offset = 44,
                type = EntityPropType.F32,
                description = "Scaled by difficulty: Normal x1, Hard x2, Very Hard x3, Ultimate x6.",
            ),
            EntityProp(
                name = "Fog on with switch",
                offset = 48,
                type = EntityPropType.F32,
                description = "1 or more: the fog is on while the switch flag is on. Otherwise it's on while the flag is off.",
            ),
            EntityProp(name = "Fog index no.", offset = 52, type = EntityPropType.I32),
            EntityProp(
                name = "Transition type",
                offset = 56,
                type = EntityPropType.I32,
                description = "0 = fade in, 1 = switch instantly.",
            ),
            EntityProp(name = "Switch ID", offset = 60, type = EntityPropType.I32),
        ),
    ),
    FixedBoxTypeRuins(
        uniqueName = "Fixed Box Type (Ruins)",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 14, 15),
        ),
        typeId = 353,
        properties = listOf(
            EntityProp(
                name = "Random drop",
                offset = 40,
                type = EntityPropType.F32,
                description = "Greater than 0: drops any item (including this floor's box rares) or nothing, and the item fields are ignored. 0 or less: drops the item the item fields define.",
            ),
            EntityProp(
                name = "Exact item",
                offset = 48,
                type = EntityPropType.F32,
                description = "0: only the item kind from item data 1 is used and bonuses, grinds etc. are randomized as usual. Otherwise: drops exactly the item defined.",
            ),
            EntityProp(
                name = "Item data 1",
                offset = 52,
                type = EntityPropType.I32,
                description = "Item definition, part 1: item class and kind, plus weapon flags (untekked, present) and special.",
            ),
            EntityProp(
                name = "Item data 2",
                offset = 56,
                type = EntityPropType.I32,
                description = "Item definition, part 2: weapon grind and first attribute, armor or unit flags, or meseta amount (in tens).",
            ),
            EntityProp(
                name = "Item data 3",
                offset = 60,
                type = EntityPropType.I32,
                description = "Item definition, part 3: weapon attributes.",
            ),
        ),
    ),
    RandomBoxTypeRuins(
        uniqueName = "Random Box Type (Ruins)",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10, 16, 17),
            Episode.II to listOf(1, 2, 3, 4, 14, 15),
        ),
        typeId = 354,
    ),
    EnemyTypeBoxYellow(
        uniqueName = "Enemy Type Box (Yellow)",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10, 16, 17),
            Episode.II to listOf(1, 2, 3, 4),
        ),
        typeId = 355,
        properties = listOf(
            EntityProp(
                name = "Event ID",
                offset = 52,
                type = EntityPropType.I32,
                description = "The wave event triggered when the box is opened.",
            ),
        ),
    ),
    EnemyTypeBoxBlue(
        uniqueName = "Enemy Type Box (Blue)",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10, 16, 17),
            Episode.II to listOf(1, 2, 3, 4),
        ),
        typeId = 356,
        properties = listOf(
            EntityProp(
                name = "Event ID",
                offset = 52,
                type = EntityPropType.I32,
                description = "The wave event triggered when the box is opened.",
            ),
        ),
    ),
    EmptyTypeBoxBlue(
        uniqueName = "Empty Type Box (Blue)",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10, 16, 17),
            Episode.II to listOf(1, 2, 3, 4),
        ),
        typeId = 357,
    ),
    DestructableRock(
        uniqueName = "Destructable Rock",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
        ),
        typeId = 358,
        properties = listOf(
            EntityProp(name = "Switch flag number", offset = 52, type = EntityPropType.I32),
        ),
    ),
    PopupTrapsTechs(
        uniqueName = "Popup Traps (techs)",
        areaIds = mapOf(
            Episode.I to listOf(6, 7, 8, 9, 10),
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
        ),
        typeId = 359,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "HP", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Action", offset = 56, type = EntityPropType.I32),
            EntityProp(name = "Tech", offset = 60, type = EntityPropType.I32),
        ),
    ),
    FlyingWhiteBird(
        uniqueName = "Flying White Bird",
        areaIds = mapOf(
            Episode.I to listOf(14, 16),
            Episode.II to listOf(3, 4),
        ),
        typeId = 368,
        properties = listOf(
            EntityProp(
                name = "Bird count delta",
                offset = 52,
                type = EntityPropType.I32,
                description = "The flock has value + 3 birds, clamped to 1 to 6; -3 gives none.",
            ),
        ),
    ),
    Tower(
        uniqueName = "Tower",
        areaIds = mapOf(
            Episode.I to listOf(14),
        ),
        typeId = 369,
    ),
    FloatingRocks(
        uniqueName = "Floating Rocks",
        areaIds = mapOf(
            Episode.I to listOf(14),
        ),
        typeId = 370,
        properties = listOf(
            EntityProp(
                name = "Spawn range delta",
                offset = 40,
                type = EntityPropType.F32,
                description = "Horizontal spawn range = value + 50.",
            ),
            EntityProp(
                name = "Number of rocks",
                offset = 52,
                type = EntityPropType.I32,
                description = "Clamped to 1 to 8.",
            ),
        ),
    ),
    FloatingSoul(
        uniqueName = "Floating Soul",
        areaIds = mapOf(
            Episode.I to listOf(14),
        ),
        typeId = 371,
    ),
    Butterfly(
        uniqueName = "Butterfly",
        areaIds = mapOf(
            Episode.I to listOf(14),
        ),
        typeId = 372,
        properties = listOf(
            EntityProp(name = "Model number?", offset = 60, type = EntityPropType.I32),
        ),
    ),
    LobbyGameMenuCollision(
        uniqueName = "Lobby Game Menu Collision",
        areaIds = mapOf(
            Episode.I to listOf(15),
        ),
        typeId = 384,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
        ),
    ),
    LobbyWarpObject(
        uniqueName = "Lobby Warp Object",
        areaIds = mapOf(
            Episode.I to listOf(15),
        ),
        typeId = 385,
        properties = listOf(
            EntityProp(name = "Hide beams", offset = 56, type = EntityPropType.I32),
        ),
    ),
    Lobby1EventObjectDefaultTree(
        uniqueName = "Lobby 1 Event Object (Default Tree)",
        areaIds = mapOf(
            Episode.I to listOf(15),
        ),
        typeId = 386,
        properties = listOf(
            EntityProp(
                name = "Default decoration",
                offset = 52,
                type = EntityPropType.I32,
                description = "The decoration shown when no lobby event is active.",
            ),
        ),
    ),
    LobbyPigeon(
        uniqueName = "Lobby Pigeon",
        areaIds = mapOf(
            Episode.I to listOf(15),
        ),
        typeId = 387,
        properties = listOf(
            EntityProp(
                name = "Path pattern",
                offset = 52,
                type = EntityPropType.I32,
                description = "Which movement path the pigeon follows (0 to 2).",
            ),
        ),
    ),
    ButterflyLobby(
        uniqueName = "Butterfly Lobby",
        areaIds = mapOf(
            Episode.I to listOf(15),
        ),
        typeId = 388,
        properties = listOf(
            EntityProp(name = "Model number", offset = 52, type = EntityPropType.I32),
        ),
    ),
    RainbowLobby(
        uniqueName = "Rainbow Lobby",
        areaIds = mapOf(
            Episode.I to listOf(15),
        ),
        typeId = 389,
    ),
    LobbyEventObjectStaticPumpkin(
        uniqueName = "Lobby Event Object (Static Pumpkin)",
        areaIds = mapOf(
            Episode.I to listOf(15),
        ),
        typeId = 390,
    ),
    LobbyEventObject3ChristmasWindows(
        uniqueName = "Lobby Event Object (3 Christmas Windows)",
        areaIds = mapOf(
            Episode.I to listOf(15),
        ),
        typeId = 391,
        properties = listOf(
            EntityProp(name = "Event flag", offset = 52, type = EntityPropType.I32),
        ),
    ),
    LobbyEventObjectRedAndWhiteCurtain(
        uniqueName = "Lobby Event Object (Red and White Curtain)",
        areaIds = mapOf(
            Episode.I to listOf(15),
        ),
        typeId = 392,
    ),
    WeddingLobby(
        uniqueName = "Wedding Lobby",
        areaIds = mapOf(
            Episode.I to listOf(15),
        ),
        typeId = 393,
    ),
    TreeLobby(
        uniqueName = "Tree Lobby",
        areaIds = mapOf(
            Episode.I to listOf(15),
        ),
        typeId = 394,
    ),
    LobbyFishTank(
        uniqueName = "Lobby Fish Tank",
        areaIds = mapOf(
            Episode.I to listOf(15),
        ),
        typeId = 395,
    ),
    LobbyEventObjectButterflies(
        uniqueName = "Lobby Event Object (Butterflies)",
        areaIds = mapOf(
            Episode.I to listOf(15),
        ),
        typeId = 396,
        properties = listOf(
            EntityProp(
                name = "Particle type",
                offset = 40,
                type = EntityPropType.F32,
                description = "Same as the Particle object's particle type.",
            ),
            EntityProp(
                name = "Param 3",
                offset = 48,
                type = EntityPropType.F32,
                description = "Same as the Particle object's param 3.",
            ),
            EntityProp(
                name = "Season",
                offset = 52,
                type = EntityPropType.I32,
                description = "The lobby event during which the particles show: 1 = Christmas, 2 = Halloween, 3 = New Year, 4 = Valentine's Day, 5 = White Day, 6 = summer, 7 = Easter, 8 = wedding, 9 = fall. Other values: no event.",
            ),
            EntityProp(
                name = "Long range (1=1500)",
                offset = 56,
                type = EntityPropType.I32,
                description = "Same as the Particle object's long range.",
            ),
            EntityProp(
                name = "Param 5",
                offset = 60,
                type = EntityPropType.I32,
                description = "Same as the Particle object's param 5.",
            ),
        ),
    ),
    Camera(
        uniqueName = "Camera",
        areaIds = mapOf(
            Episode.I to listOf(16),
            Episode.II to listOf(3, 4),
        ),
        typeId = 400,
    ),
    GreyWallLow(
        uniqueName = "grey wall low",
        areaIds = mapOf(
            Episode.I to listOf(16),
            Episode.II to listOf(3, 4, 17),
        ),
        typeId = 401,
    ),
    SpaceshipDoor(
        uniqueName = "Spaceship Door",
        areaIds = mapOf(
            Episode.I to listOf(16),
            Episode.II to listOf(3, 4),
        ),
        typeId = 402,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    GreyWallHigh(
        uniqueName = "grey wall high",
        areaIds = mapOf(
            Episode.I to listOf(16),
            Episode.II to listOf(3, 4, 17),
        ),
        typeId = 403,
    ),
    TempleNormalDoor(
        uniqueName = "Temple Normal Door",
        areaIds = mapOf(
            Episode.I to listOf(17),
            Episode.II to listOf(1, 2),
        ),
        typeId = 416,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    BreakableWallWallButUnbreakable(
        uniqueName = "\"breakable wall wall, but unbreakable\"",
        areaIds = mapOf(
            Episode.I to listOf(17),
            Episode.II to listOf(1, 2),
        ),
        typeId = 417,
    ),
    BrokenCylinderAndRubble(
        uniqueName = "Broken cylinder and rubble",
        areaIds = mapOf(
            Episode.I to listOf(17),
            Episode.II to listOf(1, 2),
        ),
        typeId = 418,
    ),
    ThreeBrokenWallPiecesOnFloor(
        uniqueName = "3 broken wall pieces on floor",
        areaIds = mapOf(
            Episode.I to listOf(17),
            Episode.II to listOf(1, 2),
        ),
        typeId = 419,
    ),
    HighBrickCylinder(
        uniqueName = "high brick cylinder",
        areaIds = mapOf(
            Episode.I to listOf(17),
            Episode.II to listOf(1, 2),
        ),
        typeId = 420,
    ),
    LyingCylinder(
        uniqueName = "lying cylinder",
        areaIds = mapOf(
            Episode.I to listOf(17),
            Episode.II to listOf(1, 2),
        ),
        typeId = 421,
    ),
    BrickConeWithFlatTop(
        uniqueName = "brick cone with flat top",
        areaIds = mapOf(
            Episode.I to listOf(17),
            Episode.II to listOf(1, 2),
        ),
        typeId = 422,
    ),
    BreakableTempleWall(
        uniqueName = "breakable temple wall",
        areaIds = mapOf(
            Episode.I to listOf(17),
            Episode.II to listOf(1, 2),
        ),
        typeId = 423,
        properties = listOf(
            EntityProp(name = "HP", offset = 52, type = EntityPropType.I32),
        ),
    ),
    TempleMapDetect(
        uniqueName = "Temple Map Detect",
        areaIds = mapOf(
            Episode.I to listOf(17),
            Episode.II to listOf(1, 2, 14),
            Episode.IV to listOf(1, 2, 3, 4, 5),
        ),
        typeId = 424,
        properties = listOf(
            EntityProp(
                name = "Lens flare",
                offset = 40,
                type = EntityPropType.F32,
                description = "When a player comes within 20 units: greater than 0 turns lens flares on, 0 or less turns them off.",
            ),
        ),
    ),
    SmallBrownBrickRisingBridge(
        uniqueName = "small brown brick rising bridge",
        areaIds = mapOf(
            Episode.I to listOf(17),
            Episode.II to listOf(1, 2),
        ),
        typeId = 425,
        properties = listOf(
            EntityProp(name = "Extra depth when lowered", offset = 40, type = EntityPropType.F32),
            EntityProp(
                name = "Rise speed",
                offset = 44,
                type = EntityPropType.F32,
                description = "Units per frame.",
            ),
            EntityProp(name = "Switch flag number", offset = 52, type = EntityPropType.I32),
        ),
    ),
    LongRisingBridgeWithPinkHighEdges(
        uniqueName = "long rising bridge (with pink high edges)",
        areaIds = mapOf(
            Episode.I to listOf(17),
            Episode.II to listOf(1, 2),
        ),
        typeId = 426,
        properties = listOf(
            EntityProp(name = "Raise Speed", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    FourSwitchTempleDoor(
        uniqueName = "4 Switch Temple Door",
        areaIds = mapOf(
            Episode.II to listOf(1, 2),
        ),
        typeId = 427,
        properties = listOf(
            EntityProp(
                name = "Switch ID",
                offset = 52,
                type = EntityPropType.I32,
                description = "Base switch flag: the door needs this flag and the ones after it. Negative: always unlocked.",
            ),
            EntityProp(
                name = "Switch count",
                offset = 56,
                type = EntityPropType.I32,
                description = "Number of switch flags the door needs (0 to 4).",
            ),
            EntityProp(
                name = "Sync mode",
                offset = 60,
                type = EntityPropType.I32,
                description = "Negative: once all switch flags are on, the door stays unlocked for good. Zero or more: it locks again when any switch flag turns off (offline single player always behaves as negative).",
            ),
        ),
    ),
    FourButtonSpaceshipDoor(
        uniqueName = "4 button Spaceship Door",
        areaIds = mapOf(
            Episode.II to listOf(3, 4),
        ),
        typeId = 448,
        properties = listOf(
            EntityProp(
                name = "Switch ID",
                offset = 52,
                type = EntityPropType.I32,
                description = "Base switch flag: the door needs this flag and the ones after it. Negative: always unlocked.",
            ),
            EntityProp(
                name = "Switch count",
                offset = 56,
                type = EntityPropType.I32,
                description = "Number of switch flags the door needs (0 to 4).",
            ),
            EntityProp(
                name = "Sync mode",
                offset = 60,
                type = EntityPropType.I32,
                description = "Negative: once all switch flags are on, the door stays unlocked for good. Zero or more: it locks again when any switch flag turns off (offline single player always behaves as negative).",
            ),
        ),
    ),
    ItemBoxCca(
        uniqueName = "Item Box CCA",
        areaIds = mapOf(
            Episode.II to listOf(5, 6, 7, 8, 9, 12, 16, 17),
            Episode.IV to listOf(5),
        ),
        typeId = 512,
    ),
    TeleporterEp2(
        uniqueName = "Teleporter (Ep. II)",
        areaIds = mapOf(
            Episode.II to listOf(5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 17),
        ),
        typeId = 513,
        properties = listOf(
            EntityProp(name = "Destination floor", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Color (0=blue, 1=red)", offset = 60, type = EntityPropType.I32),
        ),
    ),
    CcaDoor(
        uniqueName = "CCA Door",
        areaIds = mapOf(
            Episode.II to listOf(5, 6, 7, 8, 9, 16, 17),
        ),
        typeId = 514,
        properties = listOf(
            EntityProp(name = "Scale x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Scale y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Scale z", offset = 48, type = EntityPropType.F32),
            EntityProp(
                name = "Switch ID",
                offset = 52,
                type = EntityPropType.I32,
                description = "Base switch flag: the door needs this flag and the ones after it. Negative: always unlocked.",
            ),
            EntityProp(
                name = "Switch count",
                offset = 56,
                type = EntityPropType.I32,
                description = "Number of switch flags the door needs (0 to 4).",
            ),
        ),
    ),
    SpecialBoxCca(
        uniqueName = "Special Box CCA",
        areaIds = mapOf(
            Episode.II to listOf(5, 6, 7, 8, 9, 12, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5),
        ),
        typeId = 515,
        properties = listOf(
            EntityProp(
                name = "Random drop",
                offset = 40,
                type = EntityPropType.F32,
                description = "Greater than 0: drops any item (including this floor's box rares) or nothing, and the item fields are ignored. 0 or less: drops the item the item fields define.",
            ),
            EntityProp(
                name = "Exact item",
                offset = 48,
                type = EntityPropType.F32,
                description = "0: only the item kind from item data 1 is used and bonuses, grinds etc. are randomized as usual. Otherwise: drops exactly the item defined.",
            ),
            EntityProp(
                name = "Item data 1",
                offset = 52,
                type = EntityPropType.I32,
                description = "Item definition, part 1: item class and kind, plus weapon flags (untekked, present) and special.",
            ),
            EntityProp(
                name = "Item data 2",
                offset = 56,
                type = EntityPropType.I32,
                description = "Item definition, part 2: weapon grind and first attribute, armor or unit flags, or meseta amount (in tens).",
            ),
            EntityProp(
                name = "Item data 3",
                offset = 60,
                type = EntityPropType.I32,
                description = "Item definition, part 3: weapon attributes.",
            ),
        ),
    ),
    BigCcaDoor(
        uniqueName = "Big CCA Door",
        areaIds = mapOf(
            Episode.II to listOf(5),
        ),
        typeId = 516,
    ),
    BigCcaDoorSwitch(
        uniqueName = "Big CCA Door Switch",
        areaIds = mapOf(
            Episode.II to listOf(5, 6, 7, 8, 9, 16, 17),
        ),
        typeId = 517,
        properties = listOf(
            EntityProp(name = "Quest flag index", offset = 52, type = EntityPropType.I32),
        ),
    ),
    LittleRock(
        uniqueName = "Little Rock",
        areaIds = mapOf(
            Episode.II to listOf(5, 6, 7, 8, 9, 16),
        ),
        typeId = 518,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    Little3StoneWall(
        uniqueName = "Little 3 Stone Wall",
        areaIds = mapOf(
            Episode.II to listOf(5, 6, 7, 8, 9, 16),
        ),
        typeId = 519,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
        ),
    ),
    Medium3StoneWall(
        uniqueName = "Medium 3 Stone Wall",
        areaIds = mapOf(
            Episode.II to listOf(5, 6, 7, 8, 9, 16),
        ),
        typeId = 520,
    ),
    SpiderPlant(
        uniqueName = "Spider Plant",
        areaIds = mapOf(
            Episode.II to listOf(5, 6, 7, 8, 9, 16),
        ),
        typeId = 521,
        properties = listOf(
            EntityProp(name = "Model number", offset = 52, type = EntityPropType.I32, description = "0 or 1."),
        ),
    ),
    CcaAreaTeleporter(
        uniqueName = "CCA Area Teleporter",
        areaIds = mapOf(
            Episode.II to listOf(5, 6, 7, 8, 9, 16, 17),
        ),
        typeId = 522,
        properties = listOf(
            EntityProp(
                name = "Color",
                offset = 60,
                type = EntityPropType.I32,
                description = "On GameCube, 0 = red and 1 = blue.",
            ),
        ),
    ),
    LightningController(
        uniqueName = "Lightning Controller",
        areaIds = mapOf(
            Episode.II to listOf(5, 12),
        ),
        typeId = 523,
        properties = listOf(
            EntityProp(
                name = "Strike distance",
                offset = 40,
                type = EntityPropType.F32,
                description = "How far from the player lightning strikes.",
            ),
            EntityProp(name = "Lightning height", offset = 44, type = EntityPropType.F32),
            EntityProp(
                name = "Min strike interval",
                offset = 52,
                type = EntityPropType.I32,
                description = "Minimum frames between strikes.",
            ),
            EntityProp(name = "Interval randomness", offset = 56, type = EntityPropType.I32),
        ),
    ),
    WhiteBird(
        uniqueName = "White Bird",
        areaIds = mapOf(
            Episode.II to listOf(6, 7, 9, 16, 17),
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 524,
        properties = listOf(
            EntityProp(
                name = "Path pattern",
                offset = 52,
                type = EntityPropType.I32,
                description = "Which movement path the bird follows (0 to 2).",
            ),
        ),
    ),
    OrangeBird(
        uniqueName = "Orange Bird",
        areaIds = mapOf(
            Episode.II to listOf(6, 7, 9, 17),
        ),
        typeId = 525,
    ),
    ContainerJungEnemy(
        uniqueName = "Container Jung Enemy",
        areaIds = mapOf(
            Episode.II to listOf(6, 7, 9, 17),
        ),
        typeId = 526,
        properties = listOf(
            EntityProp(
                name = "Event ID",
                offset = 52,
                type = EntityPropType.I32,
                description = "The wave event triggered when the box is opened.",
            ),
            EntityProp(name = "Model number", offset = 56, type = EntityPropType.I32),
        ),
    ),
    Saw(
        uniqueName = "Saw",
        areaIds = mapOf(
            Episode.II to listOf(5, 6, 7, 8, 9, 10, 11, 16, 17),
        ),
        typeId = 527,
        properties = listOf(
            EntityProp(name = "Speed", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Model", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Arc", offset = 56, type = EntityPropType.I32),
            EntityProp(name = "SC Flag", offset = 60, type = EntityPropType.I32),
        ),
    ),
    LaserDetect(
        uniqueName = "Laser Detect",
        areaIds = mapOf(
            Episode.II to listOf(5, 6, 7, 8, 9, 10, 11, 16, 17),
        ),
        typeId = 528,
        properties = listOf(
            EntityProp(name = "Model", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Arc", offset = 56, type = EntityPropType.I32),
        ),
    ),
    BiwaMushi(
        uniqueName = "Biwa Mushi",
        areaIds = mapOf(
            Episode.II to listOf(5, 6, 7),
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 529,
    ),
    Seagull(
        uniqueName = "Seagull",
        areaIds = mapOf(
            Episode.II to listOf(5, 6, 7, 8, 9, 17),
        ),
        typeId = 530,
        properties = listOf(
            EntityProp(
                name = "Path pattern",
                offset = 52,
                type = EntityPropType.I32,
                description = "Which movement path the seagull follows (0 to 2).",
            ),
        ),
    ),
    JungleDesign(
        uniqueName = "Jungle Design",
        areaIds = mapOf(
            Episode.II to listOf(6, 7, 8, 9, 16),
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 531,
        properties = listOf(
            EntityProp(name = "Model number", offset = 52, type = EntityPropType.I32),
        ),
    ),
    Fish(
        uniqueName = "Fish",
        areaIds = mapOf(
            Episode.I to listOf(15),
            Episode.II to listOf(6, 9, 10, 11, 16),
        ),
        typeId = 544,
    ),
    SeabedDoorWithBlueEdges(
        uniqueName = "Seabed Door (with Blue Edges)",
        areaIds = mapOf(
            Episode.II to listOf(10, 11),
        ),
        typeId = 545,
        properties = listOf(
            EntityProp(name = "Scale x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Scale y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Scale z", offset = 48, type = EntityPropType.F32),
            EntityProp(
                name = "Switch ID",
                offset = 52,
                type = EntityPropType.I32,
                description = "Base switch flag: the door needs this flag and the ones after it. Negative: always unlocked.",
            ),
            EntityProp(
                name = "Switch count",
                offset = 56,
                type = EntityPropType.I32,
                description = "Number of switch flags the door needs (0 to 4).",
            ),
            EntityProp(
                name = "Sync mode",
                offset = 60,
                type = EntityPropType.I32,
                description = "Negative: once all switch flags are on, the door stays unlocked for good. Zero or more: it locks again when any switch flag turns off (offline single player always behaves as negative).",
            ),
        ),
    ),
    SeabedDoorAlwaysOpenNonTriggerable(
        uniqueName = "Seabed Door (Always Open, Non-Triggerable)",
        areaIds = mapOf(
            Episode.II to listOf(10, 11),
        ),
        typeId = 546,
    ),
    LittleCryotube(
        uniqueName = "Little Cryotube",
        areaIds = mapOf(
            Episode.II to listOf(10, 11, 17),
        ),
        typeId = 547,
        properties = listOf(
            EntityProp(name = "Model", offset = 52, type = EntityPropType.I32),
        ),
    ),
    WideGlassWallBreakable(
        uniqueName = "Wide Glass Wall (Breakable)",
        areaIds = mapOf(
            Episode.II to listOf(10, 11),
        ),
        typeId = 548,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Model", offset = 56, type = EntityPropType.I32),
        ),
    ),
    BlueFloatingRobot(
        uniqueName = "Blue Floating Robot",
        areaIds = mapOf(
            Episode.II to listOf(10, 11),
        ),
        typeId = 549,
    ),
    RedFloatingRobot(
        uniqueName = "Red Floating Robot",
        areaIds = mapOf(
            Episode.II to listOf(10, 11),
        ),
        typeId = 550,
    ),
    Dolphin(
        uniqueName = "Dolphin",
        areaIds = mapOf(
            Episode.II to listOf(10, 11),
        ),
        typeId = 551,
        properties = listOf(
            EntityProp(
                name = "Path pattern",
                offset = 52,
                type = EntityPropType.I32,
                description = "Which movement path the dolphin follows (0 to 4).",
            ),
        ),
    ),
    CaptureTrap(
        uniqueName = "Capture Trap",
        areaIds = mapOf(
            Episode.II to listOf(5, 6, 7, 8, 9, 10, 11, 16, 17),
        ),
        typeId = 552,
        properties = listOf(
            EntityProp(name = "Speed", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Damage", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Duration", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Invisible", offset = 56, type = EntityPropType.I32),
            EntityProp(name = "Tech", offset = 60, type = EntityPropType.I32),
        ),
    ),
    VRLink(
        uniqueName = "VR Link",
        areaIds = mapOf(
            Episode.II to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
        ),
        typeId = 553,
        properties = listOf(
            EntityProp(name = "Switch ID", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Script label", offset = 60, type = EntityPropType.I32),
        ),
    ),
    Ep2Particle(
        uniqueName = "EP2 Particle",
        areaIds = mapOf(
            Episode.II to listOf(12),
        ),
        typeId = 576,
        // Type 0x0240 is an exact constructor alias for TObjParticle (0x0001).
        properties = listOf(
            EntityProp(name = "Particle type", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Param 2", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Param 3", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Long range", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Param 5", offset = 56, type = EntityPropType.I32),
            EntityProp(name = "Param 6", offset = 60, type = EntityPropType.I32),
        ),
    ),
    WarpInBarbaRayRoom(
        uniqueName = "Warp in Barba Ray Room",
        areaIds = mapOf(
            Episode.II to listOf(14),
        ),
        typeId = 640,
        properties = listOf(
            EntityProp(name = "Destination floor", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Color (0=blue, 1=red)", offset = 60, type = EntityPropType.I32),
        ),
    ),
    LiveCamera(
        uniqueName = "Live Camera",
        areaIds = mapOf(
            Episode.II to listOf(15),
        ),
        typeId = 672,
    ),
    GeeNest(
        uniqueName = "Gee Nest",
        areaIds = mapOf(
            Episode.I to listOf(8, 9, 10),
            Episode.II to listOf(5, 6, 7, 8, 9, 16, 17),
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 688,
        properties = listOf(
            EntityProp(
                name = "Idle duration",
                offset = 40,
                type = EntityPropType.F32,
                description = "Maximum idle time in frames = value + 299; ends early when a player comes within 80 units.",
            ),
            EntityProp(
                name = "Spew duration",
                offset = 44,
                type = EntityPropType.F32,
                description = "Poison-spewing time in frames = value + 209.",
            ),
            EntityProp(
                name = "Poison radius squared",
                offset = 48,
                type = EntityPropType.F32,
                description = "Squared poison radius = value + 399.",
            ),
            EntityProp(
                name = "Particle interval",
                offset = 60,
                type = EntityPropType.I32,
                description = "Frames between particle bursts while spewing = value + 4.",
            ),
        ),
    ),
    LabComputerConsole(
        uniqueName = "Lab Computer Console",
        areaIds = mapOf(
            Episode.II to listOf(0),
        ),
        typeId = 689,
    ),
    LabComputerConsoleGreenScreen(
        uniqueName = "Lab Computer Console (Green Screen)",
        areaIds = mapOf(
            Episode.II to listOf(0),
        ),
        typeId = 690,
    ),
    ChairYellowPillow(
        uniqueName = "Chair, Yellow Pillow",
        areaIds = mapOf(
            Episode.II to listOf(0),
        ),
        typeId = 691,
    ),
    OrangeWallWithHoleInMiddle(
        uniqueName = "Orange Wall with Hole in Middle",
        areaIds = mapOf(
            Episode.II to listOf(0),
        ),
        typeId = 692,
    ),
    GreyWallWithHoleInMiddle(
        uniqueName = "Grey Wall with Hole in Middle",
        areaIds = mapOf(
            Episode.II to listOf(0),
        ),
        typeId = 693,
    ),
    LongTable(
        uniqueName = "Long Table",
        areaIds = mapOf(
            Episode.II to listOf(0),
        ),
        typeId = 694,
    ),
    GBAStation(
        uniqueName = "GBA Station",
        areaIds = mapOf(),
        typeId = 695,
        properties = listOf(
            EntityProp(name = "Script label", offset = 52, type = EntityPropType.I32),
        ),
    ),
    TalkLinkToSupport(
        uniqueName = "Talk (Link to Support)",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 0),
        ),
        typeId = 696,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Script label", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Activator", offset = 56, type = EntityPropType.I32),
        ),
    ),
    InstaWarp(
        uniqueName = "Insta-Warp",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 11, 12, 13, 14, 16, 17),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 0),
        ),
        typeId = 697,
        properties = listOf(
            EntityProp(name = "Destination x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Destination y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Destination z", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Dst. rotation y", offset = 52, type = EntityPropType.Angle),
            EntityProp(name = "Displayed floor number", offset = 56, type = EntityPropType.I32),
            EntityProp(
                name = "Hide floor number",
                offset = 60,
                type = EntityPropType.I32,
                description = "0 or less: show the floor number after warping. Greater than 0: hide it.",
            ),
        ),
    ),
    QuestCollision2(
        uniqueName = "Quest Collision 2",
        areaIds = mapOf(
            Episode.I to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14),
            Episode.II to listOf(0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17),
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 0),
        ),
        typeId = 698,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Script label", offset = 52, type = EntityPropType.I32),
            EntityProp(
                name = "Script manager (<=0=quest, >0=free play)",
                offset = 56,
                type = EntityPropType.I32,
            ),
        ),
    ),
    LabGlassWindowDoor(
        uniqueName = "Lab Glass Window Door",
        areaIds = mapOf(
            Episode.II to listOf(0),
        ),
        typeId = 699,
    ),
    AreaWarpEndingJung(
        uniqueName = "Area Warp Ending Jung",
        areaIds = mapOf(
            Episode.II to listOf(13),
        ),
        typeId = 700,
    ),
    // newserv: 0x02BD TObjLaboMapWarp, the visible same-map warp between the Lab and city.
    LabMapWarp(
        uniqueName = "Lab Map Warp",
        areaIds = mapOf(
            Episode.II to listOf(0),
        ),
        typeId = 701,
        properties = listOf(
            EntityProp(name = "Destination x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Destination y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Destination z", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Dst. rotation y", offset = 52, type = EntityPropType.Angle),
            EntityProp(name = "Destination text", offset = 60, type = EntityPropType.I32),
        ),
    ),
    Ep4LightSource(
        uniqueName = "Ep. IV Light Source",
        areaIds = mapOf(
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8, 9),
        ),
        typeId = 768,
    ),
    Cactus(
        uniqueName = "Cactus",
        areaIds = mapOf(
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 769,
        properties = listOf(
            EntityProp(name = "Scale x", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Scale y", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Scale z", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Model", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Damage Power", offset = 56, type = EntityPropType.I32),
        ),
    ),
    BigBrownRock(
        uniqueName = "Big Brown Rock",
        areaIds = mapOf(
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 770,
        properties = listOf(
            EntityProp(name = "Model", offset = 52, type = EntityPropType.I32),
        ),
    ),
    BreakableBrownRock(
        uniqueName = "Breakable Brown Rock",
        areaIds = mapOf(
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 771,
        properties = listOf(
            EntityProp(name = "Switch", offset = 52, type = EntityPropType.I32),
        ),
    ),
    UnknownItem832(
        uniqueName = "Unknown Item (832)",
        areaIds = mapOf(),
        typeId = 832,
        properties = listOf(
            EntityProp(name = "Object identifier", offset = 52, type = EntityPropType.I32),
        ),
    ),
    UnknownItem833(
        uniqueName = "Unknown Item (833)",
        areaIds = mapOf(),
        typeId = 833,
        properties = listOf(
            EntityProp(name = "Object identifier", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Child index?", offset = 56, type = EntityPropType.I32),
        ),
    ),
    PoisonPlant(
        uniqueName = "Poison Plant",
        areaIds = mapOf(
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 896,
    ),
    UnknownItem897(
        uniqueName = "Unknown Item (897)",
        areaIds = mapOf(
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 897,
        properties = listOf(
            EntityProp(name = "Model number", offset = 52, type = EntityPropType.I32),
        ),
    ),
    UnknownItem898(
        uniqueName = "Unknown Item (898)",
        areaIds = mapOf(
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 898,
    ),
    OozingDesertPlant(
        uniqueName = "Oozing Desert Plant",
        areaIds = mapOf(
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 899,
        properties = listOf(
            EntityProp(name = "Animation speed?", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Scale factor", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Model number", offset = 52, type = EntityPropType.I32),
        ),
    ),
    UnknownItem901(
        uniqueName = "Unknown Item (901)",
        areaIds = mapOf(
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 901,
        properties = listOf(
            EntityProp(name = "Animation speed?", offset = 40, type = EntityPropType.F32),
        ),
    ),
    BigBlackRocks(
        uniqueName = "Big Black Rocks",
        areaIds = mapOf(
            Episode.IV to listOf(1, 2, 3, 4, 5, 6, 7, 8),
        ),
        typeId = 902,
        properties = listOf(
            EntityProp(name = "Model", offset = 52, type = EntityPropType.I32),
        ),
    ),
    UnknownItem903(
        uniqueName = "Unknown Item (903)",
        areaIds = mapOf(
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 903,
        properties = listOf(
            EntityProp(name = "Area radius", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Area power", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Hole radius", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Hole power", offset = 56, type = EntityPropType.I32),
        ),
    ),
    UnknownItem904(
        uniqueName = "Unknown Item (904)",
        areaIds = mapOf(
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 904,
        properties = listOf(
            EntityProp(name = "Hitbox width", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Hitbox radius", offset = 44, type = EntityPropType.F32),
            EntityProp(name = "Hitbox depth", offset = 48, type = EntityPropType.F32),
            EntityProp(name = "Hitbox type", offset = 60, type = EntityPropType.I32),
        ),
    ),
    UnknownItem905(
        uniqueName = "Unknown Item (905)",
        areaIds = mapOf(),
        typeId = 905,
        properties = listOf(
            EntityProp(name = "Game flags to set", offset = 52, type = EntityPropType.I32),
            EntityProp(name = "Game flags to clear", offset = 56, type = EntityPropType.I32),
            EntityProp(
                name = "Hitbox type",
                offset = 60,
                type = EntityPropType.I32,
                description = "0 = rectangular, otherwise cylindrical.",
            ),
        ),
    ),
    UnknownItem906(
        uniqueName = "Unknown Item (906)",
        areaIds = mapOf(),
        typeId = 906,
        properties = listOf(
            EntityProp(name = "Interval", offset = 56, type = EntityPropType.I32),
            EntityProp(
                name = "Hitbox type",
                offset = 60,
                type = EntityPropType.I32,
                description = "0 = rectangular, otherwise cylindrical.",
            ),
        ),
    ),
    FallingRock(
        uniqueName = "Falling Rock",
        areaIds = mapOf(
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 907,
    ),
    DesertPlantHasCollision(
        uniqueName = "Desert Plant (Has Collision)",
        areaIds = mapOf(
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 908,
        properties = listOf(
            EntityProp(name = "Horizontal scale factor", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Vertical scale factor", offset = 44, type = EntityPropType.F32),
        ),
    ),
    DesertFixedTypeBoxBreakableCrystals(
        uniqueName = "Desert Fixed Type Box (Breakable Crystals)",
        areaIds = mapOf(
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 909,
        properties = listOf(
            EntityProp(
                name = "Contents type",
                offset = 40,
                type = EntityPropType.F32,
                description = "0 = empty, 1 = random item, 2 = the item the item fields define, 3 = triggers an event.",
            ),
            EntityProp(
                name = "Exact item",
                offset = 48,
                type = EntityPropType.F32,
                description = "Contents type 2: 0 = only the item kind from item data 1 is used and the rest is randomized; otherwise drops exactly the item defined.",
            ),
            EntityProp(
                name = "Event ID",
                offset = 52,
                type = EntityPropType.I32,
                description = "Contents type 3: the wave event triggered when the box is opened. Contents type 2: item definition, part 1 (item class and kind, plus weapon flags and special).",
            ),
            EntityProp(
                name = "Item data 2",
                offset = 56,
                type = EntityPropType.I32,
                description = "Contents type 2: item definition, part 2.",
            ),
            EntityProp(
                name = "Item data 3",
                offset = 60,
                type = EntityPropType.I32,
                description = "Contents type 2: item definition, part 3.",
            ),
        ),
    ),
    Ep4TestDoor(
        uniqueName = "EP4 Test Door",
        areaIds = mapOf(),
        typeId = 910,
    ),
    BeeHive(
        uniqueName = "Bee Hive",
        areaIds = mapOf(
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 911,
        properties = listOf(
            EntityProp(name = "Model", offset = 52, type = EntityPropType.I32),
        ),
    ),
    Ep4TestParticle(
        uniqueName = "EP4 Test Particle",
        areaIds = mapOf(
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 912,
        properties = listOf(
            EntityProp(name = "Particle distance?", offset = 40, type = EntityPropType.F32),
            EntityProp(name = "Frames between effects", offset = 52, type = EntityPropType.I32),
        ),
    ),
    Heat(
        uniqueName = "Heat",
        areaIds = mapOf(
            Episode.IV to listOf(6, 7, 8),
        ),
        typeId = 913,
        properties = listOf(
            EntityProp(name = "Radius", offset = 40, type = EntityPropType.F32),
            EntityProp(
                name = "Poison power",
                offset = 44,
                type = EntityPropType.F32,
                description = "Scaled by difficulty: Normal x1, Hard x2, Very Hard x3, Ultimate x6.",
            ),
            EntityProp(
                name = "Fog on with switch",
                offset = 48,
                type = EntityPropType.F32,
                description = "1 or more: the fog is on while the switch flag is on. Otherwise it's on while the flag is off.",
            ),
            EntityProp(name = "Fog index no.", offset = 52, type = EntityPropType.I32),
            EntityProp(
                name = "Transition type",
                offset = 56,
                type = EntityPropType.I32,
                description = "0 = fade in, 1 = switch instantly.",
            ),
            EntityProp(name = "Switch ID", offset = 60, type = EntityPropType.I32),
        ),
    ),
    TopOfSaintMillionEgg(
        uniqueName = "Top of Saint Million Egg",
        areaIds = mapOf(
            Episode.IV to listOf(9),
        ),
        typeId = 960,
    ),
    Ep4BossRockSpawner(
        uniqueName = "EP4 Boss Rock Spawner",
        areaIds = mapOf(
            Episode.IV to listOf(9),
        ),
        typeId = 961,
        properties = listOf(
            EntityProp(name = "Type", offset = 52, type = EntityPropType.I32),
        ),
    );

    override val simpleName = uniqueName

    companion object {
        /**
         * Use this instead of [values] to avoid unnecessary copying.
         */
        val VALUES: Array<ObjectType> = entries.toTypedArray()
    }
}
